import { DATA_BASE, FETCH_CONCURRENCY } from '../config';
import { classifier } from './canopyClient';

/**
 * Loads a tree's LiDAR canopy from the frozen 2023 archive.
 *
 * The archive does not cover every tree in the census. Sampling 200 random
 * tree_ids from the source GeoJSON against the bucket (2026-08-07):
 *
 *   86.5%  populated
 *    9.0%  present but `[]` -- the tree is in the census, no canopy extracted
 *    4.5%  absent entirely  -- 404, no object at that key
 *
 * Those last two are the same fact to a visitor, so a 404 resolves to an empty
 * array rather than throwing. Reserving the thrown error for genuine transport
 * failures is what lets the UI say "no canopy was extracted" (true, and final)
 * instead of "could not load" (which implies retrying might help).
 *
 * The 2023 site added a point cloud layer regardless and so did nothing
 * visible for all 13.5%.
 *
 * What is cached here is only the point *count*. The clouds themselves go
 * straight to the classifier, which owns them from then on (lib/canopyClient.ts)
 * -- 250 trees is about 34 MB of coordinates, and keeping a second copy on this
 * side just to answer `isCached` would double that for nothing.
 */
const counts = new Map<number, number>();
const inFlight = new Map<number, Promise<number>>();

/**
 * Flattens the archive's `[[lat, lon, height], ...]` into one Float64Array of
 * `[lon, lat, height, ...]` -- deck.gl's order, so the canopy positions can be
 * copied into the render buffer whole rather than a coordinate at a time.
 *
 * A 5,773-point cloud arrives as 5,773 separate JS arrays; at 250 trees that is
 * 1.4 million of them, each with its own header, walked on every sun move. The
 * flat form is also what makes the worker hand-off zero-copy.
 *
 * Float64, not Float32, despite the extra 12 MB: at longitude -73.98 a float32
 * step is 8.8e-6 degrees, about 0.75 m, which would visibly quantise the canopy
 * and shift points across the building edges this whole model turns on.
 */
function flatten(raw: number[][]): Float64Array {
  const out = new Float64Array(raw.length * 3);
  for (let i = 0; i < raw.length; i++) {
    const p = raw[i];
    const o = i * 3;
    out[o] = p[1];
    out[o + 1] = p[0];
    out[o + 2] = p[2];
  }
  return out;
}

/**
 * Loads a canopy and hands it to the classifier, resolving with how many points
 * it holds.
 *
 * Returning the count rather than the cloud is deliberate: nothing on this side
 * of the app reads coordinates any more. The classifier gives back positions
 * and colors already packed for deck.gl, so the only copy of the raw cloud
 * lives wherever the classification runs.
 */
export async function loadPointCloud(treeId: number): Promise<number> {
  const cached = counts.get(treeId);
  if (cached !== undefined) return cached;

  const pending = inFlight.get(treeId);
  if (pending) return pending;

  const request = (async () => {
    const res = await fetch(`${DATA_BASE}/treeJsonFiles/2021/${treeId}.json`);
    // 404 means the archive has no canopy for this tree -- indistinguishable,
    // to a visitor, from the ones stored as `[]`. Record it so a second click
    // does not re-request a key that will never exist.
    if (res.status === 404) {
      counts.set(treeId, 0);
      inFlight.delete(treeId);
      return 0;
    }
    if (!res.ok) {
      throw new Error(`Point cloud ${treeId} unavailable (HTTP ${res.status})`);
    }
    const points = flatten((await res.json()) as number[][]);
    const count = points.length / 3;
    if (count > 0) classifier.putCloud(treeId, points);
    counts.set(treeId, count);
    inFlight.delete(treeId);
    return count;
  })();

  inFlight.set(treeId, request);
  try {
    return await request;
  } catch (err) {
    inFlight.delete(treeId);
    throw err;
  }
}

/** True when this tree's cloud is already in memory, so loading is free. */
export function isCached(treeId: number): boolean {
  return counts.has(treeId);
}

/**
 * Loads many point clouds with bounded concurrency, reporting each as it
 * arrives so the map fills in progressively rather than in one late jump.
 *
 * `signal` lets a superseded viewport abandon its remaining fetches -- without
 * it, panning quickly would queue every intermediate viewport's worth of trees.
 * Failures are dropped silently: a single unreachable cloud should not
 * interrupt loading the rest.
 *
 * `onSettled` reports how many requests have finished, which is a different
 * question from how many canopies arrived and needs its own callback. About 17%
 * of the census has nothing to draw (see loadPointCloud), and those resolve
 * without ever calling `onLoaded` -- so a progress indicator driven off
 * `onLoaded` would stall a little short of the end on most viewports, which is
 * the one thing a progress indicator must never do.
 */
export async function loadPointClouds(
  treeIds: number[],
  onLoaded: (treeId: number, pointCount: number) => void,
  signal?: AbortSignal,
  onSettled?: (settled: number) => void,
): Promise<void> {
  let next = 0;
  let settled = 0;

  const worker = async () => {
    while (next < treeIds.length) {
      if (signal?.aborted) return;
      const id = treeIds[next++];
      try {
        const count = await loadPointCloud(id);
        if (signal?.aborted) return;
        if (count > 0) onLoaded(id, count);
      } catch {
        // Transport failure on one tree; keep going.
      }
      // Counted whatever the outcome -- rendered, empty, or 404. All three are
      // equally finished, and only the first is visible on the map.
      if (signal?.aborted) return;
      onSettled?.(++settled);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(FETCH_CONCURRENCY, treeIds.length) }, worker),
  );
}
